import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { ShieldCheck, Printer } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = { withCredentials: true };

// Acacia office: issue a stamped pre-authorization code for a member before they visit a pre-auth hospital.
export default function PreauthCodeDialog({ open, onOpenChange, member, onIssued }) {
  const [hospitals, setHospitals] = useState([]);
  const [form, setForm] = useState({ hospital_name: '', reason: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);

  useEffect(() => {
    if (!open) return;
    setForm({ hospital_name: '', reason: '', notes: '' }); setIssued(null);
    axios.get(`${API}/admin/hospitals`, authCfg).then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data.hospitals || []);
      setHospitals(list.filter((h) => h.preauth_required));
    }).catch(() => setHospitals([]));
  }, [open]);

  const issue = async () => {
    if (!form.hospital_name) { toast.error('Choose the pre-authorization hospital'); return; }
    if (!form.reason.trim()) { toast.error('Enter the reason / treatment being authorized'); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/preauth-codes`, { patient_serial_number: member.serial_number, ...form }, authCfg);
      setIssued(res.data);
      toast.success(`Pre-authorization code ${res.data.code} issued`);
      onIssued?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not issue the code');
    } finally {
      setBusy(false);
    }
  };

  if (!member) return null;
  const name = member.patient_name || [member.first_name, member.middle_name, member.last_name].filter(Boolean).join(' ');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="preauth-code-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-amber-600" /> Pre-authorize — {name}</DialogTitle>
          <DialogDescription>{member.serial_number} • {member.family_id}. The code is single-use, valid 14 days, only at the chosen hospital. Print the form, stamp it and give it to the patient.</DialogDescription>
        </DialogHeader>
        {issued ? (
          <div className="space-y-3" data-testid="preauth-code-result">
            <div className="rounded-lg border-2 border-amber-500 bg-amber-50 p-4 text-center">
              <p className="text-xs uppercase text-amber-800 font-semibold">Pre-authorization code</p>
              <p className="text-3xl font-mono font-bold tracking-widest text-amber-900" data-testid="preauth-code-value">{issued.code}</p>
              <p className="text-xs text-amber-800 mt-1">{issued.hospital_name} • valid until {new Date(issued.expires_at).toLocaleDateString('en-GB')}</p>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
              <Button onClick={() => window.open(`/print-preauth/${issued.code}`, '_blank')} className="bg-amber-600 hover:bg-amber-700" data-testid="preauth-code-print"><Printer className="w-4 h-4 mr-1" /> Print Pre-authorization Form</Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              <div>
                <Label className="text-sm">Hospital (pre-authorization required) <span className="text-red-500">*</span></Label>
                <select value={form.hospital_name} onChange={(e) => setForm((p) => ({ ...p, hospital_name: e.target.value }))} className="mt-1 w-full h-9 rounded-md border border-gray-300 px-2 text-sm bg-white" data-testid="preauth-code-hospital">
                  <option value="">Choose a hospital…</option>
                  {hospitals.map((h) => <option key={h.hospital_name} value={h.hospital_name}>{h.hospital_name}</option>)}
                </select>
                {hospitals.length === 0 && <p className="text-xs text-amber-700 mt-1">No hospital has pre-authorization switched on (Admin Panel → Hospitals).</p>}
              </div>
              <div>
                <Label className="text-sm">Reason / treatment authorized <span className="text-red-500">*</span></Label>
                <textarea value={form.reason} onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))} rows={3} className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm" placeholder="e.g. Specialist review — orthopaedic surgeon, knee pain" data-testid="preauth-code-reason" />
              </div>
              <div>
                <Label className="text-sm">Notes for the hospital (optional)</Label>
                <Input value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} placeholder="e.g. Covers consultation and X-ray only" className="mt-1" data-testid="preauth-code-notes" />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={issue} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="preauth-code-issue">{busy ? 'Issuing…' : 'Generate Code'}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
